import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { useTerminalLoadQuery, useTerminalUpdateMutation } from "src/app/features/Terminal/services/server/TerminalApi";
import { keepTerminalProfileReadOnlyFlagsUtils, updateWatchedFieldsUtils } from "src/app/features/Terminal/TerminalProfileToTerminalUtils";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { useTerminalProfileLoadQuery } from "src/app/features/TerminalProfile/services/server/TerminalProfileApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";

export const TerminalEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalUpdateMutation();

    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.TERMINALS}`);
    const { id } = useParams();

    const [formData, setFormData] = useState<TerminalEntity>();
    const prevProfileIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<TerminalEntity | null>(null);
    const formDataRef = useRef(formData);
    const [terminalProfileId, setTerminalProfileId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("terminal");
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useTerminalLoadQuery({ entityId: id });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
            formDataRef.current = loadFormData;
            prevProfileIdRef.current = loadFormData.terminalProfileId;
            setTerminalProfileId(loadFormData.terminalProfileId);
        }
    }, [loadFormData]);

    const applyUpdatedData = useCallback((updatedData: TerminalEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.terminalProfileId !== prevProfileIdRef.current) {
            setTerminalProfileId(updatedData.terminalProfileId);
            setFormData(updatedData);

            if (updatedData.terminalProfileId === undefined) {
                prevProfileIdRef.current = undefined;
            }
        }
    }, []);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: TerminalEntity) => {
            const updatedData = keepTerminalProfileReadOnlyFlagsUtils(newData, formDataRef.current);

            const hasProfileChange = updatedData.terminalProfileId !== prevProfileIdRef.current;
            if (hasProfileChange && updatedData.terminalProfileId !== undefined) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...(formDataRef.current as TerminalEntity) });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData],
    );

    const onConfirmProfileChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelProfileChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...(formDataRef.current as TerminalEntity) });
    }, []);

    const { data: terminalProfileEntity } = useTerminalProfileLoadQuery(
        { entityId: terminalProfileId ? `${terminalProfileId}` : undefined },
        { skip: !terminalProfileId },
    );

    // HANDLE WATCHED FIELDS
    const updateWatchedFields = useCallback(() => {
        const currentProfileId = formDataRef.current?.terminalProfileId;

        if (!currentProfileId || !terminalProfileEntity || String(terminalProfileEntity.id) !== String(currentProfileId)) return null;

        prevProfileIdRef.current = currentProfileId;
        if (!formDataRef.current) return null;

        return updateWatchedFieldsUtils(terminalProfileEntity, formDataRef.current);
    }, [terminalProfileEntity]);

    useEffect(() => {
        if (!terminalProfileId || !terminalProfileEntity || String(terminalProfileEntity.id) !== String(terminalProfileId)) {
            return;
        }

        const prevProfileId = prevProfileIdRef.current;

        if (terminalProfileId !== prevProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [terminalProfileId, terminalProfileEntity, updateWatchedFields]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formDataRef.current) return;

            const result = await saveFormData({ data: formDataRef.current });
            const hasError = "error" in result;

            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "Terminal" }));
            }
        },
        [saveFormData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && formData && (
                <FormManager<TerminalEntity>
                    name="TerminalEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmProfileChange}
                onCancel={onCancelProfileChange}
            />
        </Spin>
    );
};
